/*
===============================================================================
CUSTOM RING SELECTOR - VEDLIKEHOLDSGUIDE
===============================================================================

Denne filen bygger en dynamisk produktvelger for ringprodukter.

HVORDAN LEGGE TIL EN NY VALGTYPE
Eksempel: "Fasong" med JSON-feltene `shapes` og `shape`.

1. LEGG FELTET TIL I `fields` NEDENFOR

   { id: "ring_selector_shape", key: "shape", source: "shapes" },

   - `id` må være lik ID-en i Liquid.
   - `key` er egenskapen på hvert produkt i JSON-filen.
   - `source` er arrayet med tilgjengelige valg øverst i JSON-filen.

2. LEGG ARRAYET TIL I `loadJSON()`

   shapes: Array.isArray(raw.shapes) ? raw.shapes : [],

3. LEGG FELTET TIL PÅ HVERT PRODUKT I JSON-FILEN

   {
     "sku": "EKSEMPEL-SKU",
     "handle": "produkt-handle",
     "ring_size": "56",
     "shape": "OVAL"
   }

   Arrayet kan enten inneholde enkle verdier:

   "shapes": ["OVAL", "RUND"]

   eller verdier med egne etiketter:

   "shapes": [
     { "value": "OVAL", "label": "Oval" },
     { "value": "ROUND", "label": "Rund" }
   ]

4. LEGG FELTET TIL I LIQUID-MALEN

   <div
     id="ring_selector_shape"
     class="gifteringer-selector__field"
     data-option-key="shape"
     data-option-source="shapes"
     data-option-label="Fasong"
     data-option-type="block"
     data-option-suffix=""
     hidden
   ></div>

   `data-option-type` kan være:
   - "block" for knapper
   - "dropdown" for nedtrekksliste

VIKTIG OM REKKEFØLGEN
Rekkefølgen i `fields` bestemmer avhengigheten mellom valgene.
Et felt blir filtrert av alle aktive felt som står over det.

Eksempel:
- Ringstørrelse
- Karat
- Diamanttype
- Materiale

Da avhenger karat av ringstørrelse, diamanttype av ringstørrelse + karat,
og materiale av alle valgene over.

ET FELT ER KUN AKTIVT NÅR
- feltet finnes i Liquid, og
- arrayet i JSON-filen inneholder minst én verdi.

Det betyr at Liquid kan inneholde alle fremtidige felt. Et tomt JSON-array,
for eksempel `"widths": []`, skjuler feltet og fjerner det fra logikken.

SPESIALT FOR DIAMANTTYPE
Diamanttype finnes ikke nødvendigvis i JSON-filen. Den leses fra SKU-en:
- `-LGD-` blir "Laboratoriedyrket diamant"
- `-ND-` blir "Naturlig diamant"

===============================================================================
*/

(function () {
  // Cache hindrer at samme JSON-fil lastes flere ganger.
  const cache = {};

  // Alle mulige valgtyper. Rekkefølgen styrer avhengigheten mellom dem.
  const fields = [
    { id: "ring_selector_size", key: "ring_size", source: "ring_sizes" },
    { id: "ring_selector_width", key: "width", source: "widths" },
    { id: "ring_selector_height", key: "height", source: "heights" },
    { id: "ring_selector_karat", key: "karat", source: "karats" },
    {
      id: "ring_selector_diamond_type",
      key: "diamond_type",
      source: "diamond_types",
    },
    { id: "ring_selector_surface", key: "surface", source: "surfaces" },
    { id: "ring_selector_material", key: "material", source: "materials" },
  ];

  // Sammenlign verdier uten problemer med mellomrom eller små/store bokstaver.
  function normalize(value) {
    return String(value ?? "")
      .trim()
      .toUpperCase();
  }

  function log() {
    console.log("[CUSTOM RING]", ...arguments);
  }

  // Leser LGD eller ND fra SKU, for eksempel: 609419-HV-LGD-034-56.
  function getDiamondTypeFromSku(sku) {
    const match = String(sku || "")
      .toUpperCase()
      .match(/-(LGD|ND)-/);

    return match ? match[1] : "";
  }

  // Laster og klargjør JSON-data. Nye JSON-arrays registreres her.
  async function loadJSON(url) {
    if (cache[url]) return cache[url];

    const response = await fetch(url, {
      credentials: "same-origin",
    });

    if (!response.ok) {
      throw new Error(`JSON load failed: ${response.status}`);
    }

    const raw = await response.json();

    // Hvert valg-array bør alltid ende som et array, også når det mangler.
    const data = {
      ring_sizes: Array.isArray(raw.ring_sizes) ? raw.ring_sizes : [],
      widths: Array.isArray(raw.widths) ? raw.widths : [],
      heights: Array.isArray(raw.heights) ? raw.heights : [],
      karats: Array.isArray(raw.karats) ? raw.karats : [],
      // Diamanttypene er faste valg. Produktets verdi hentes fra SKU under.
      diamond_types: [
        { value: "LGD", label: "Lab-grown diamant" },
        { value: "ND", label: "Naturlig diamant" },
      ],
      surfaces: Array.isArray(raw.surfaces) ? raw.surfaces : [],
      materials: Array.isArray(raw.materials) ? raw.materials : [],
      // Legger `diamond_type` på hvert produkt, utledet fra SKU ved behov.
      products: Array.isArray(raw.products)
        ? raw.products.map((product) => ({
            ...product,
            diamond_type: normalize(
              product.diamond_type || getDiamondTypeFromSku(product.sku),
            ),
          }))
        : [],
    };

    cache[url] = data;
    log("Loaded products:", data.products.length);

    return data;
  }

  function optionValue(option) {
    if (option && typeof option === "object") {
      return String(option.value ?? "");
    }

    return String(option ?? "");
  }

  function optionLabel(option) {
    if (option && typeof option === "object") {
      return String(option.label ?? option.value ?? "");
    }

    return String(option ?? "");
  }

  function getCurrentProduct(data, selector) {
    const currentSku = normalize(selector.dataset.currentSku);

    return data.products.find((product) => {
      return normalize(product.sku) === currentSku;
    });
  }

  // Et felt er bare aktivt når det finnes i Liquid OG har valg i JSON/data.
  function getActiveFields(selector, data) {
    return fields.filter((field) => {
      const element = selector.querySelector("#" + field.id);
      const options = data?.[field.source];

      return Boolean(element && Array.isArray(options) && options.length > 0);
    });
  }

  function getStateFromProduct(product, activeFields = fields) {
    const state = {};

    activeFields.forEach((field) => {
      state[field.key] = product ? normalize(product[field.key]) : "";
    });

    return state;
  }

  function getValue(selector, id) {
    const field = selector.querySelector("#" + id);
    if (!field || field.hidden) return "";

    const active = field.querySelector(
      ".gifteringer-selector__option.is-active",
    );

    if (active) return normalize(active.value);

    const select = field.querySelector(".gifteringer-selector__select");

    if (select) return normalize(select.value);

    return "";
  }

  function getState(selector) {
    const state = {};

    getActiveFields(selector, selector._customRingerData).forEach((field) => {
      state[field.key] = getValue(selector, field.id);
    });

    return state;
  }

  function createLabel(field, text) {
    const label = document.createElement("label");
    label.className = "gifteringer-selector__label";
    label.textContent = text;
    field.appendChild(label);
  }

  // Bygger knappvisning og markerer utilgjengelige kombinasjoner som disabled.
  function createButtons(field, options, selectedValue, suffix) {
    const wrapper = document.createElement("div");
    wrapper.className = "gifteringer-selector__options";

    options.forEach((option) => {
      const value = optionValue(option);
      const label = optionLabel(option);
      const available = option.available !== false;

      const button = document.createElement("button");
      button.type = "button";
      button.value = value;
      button.className = "gifteringer-selector__option";
      button.textContent = `${label}${suffix}`;
      button.disabled = !available;
      button.setAttribute("aria-disabled", String(!available));

      if (!available) {
        button.classList.add("is-disabled");
        button.title = "Ikke tilgjengelig med valgene over";
      }

      if (available && normalize(value) === normalize(selectedValue)) {
        button.classList.add("is-active");
      }

      wrapper.appendChild(button);
    });

    field.appendChild(wrapper);
  }

  // Bygger nedtrekksliste når Liquid-feltet bruker data-option-type="dropdown".
  function createDropdown(field, options, selectedValue, labelText, suffix) {
    const wrapper = document.createElement("div");
    wrapper.className = "gifteringer-selector__select-wrap";

    const select = document.createElement("select");
    select.className = "gifteringer-selector__select";

    const placeholder = document.createElement("option");
    placeholder.value = "";
    placeholder.textContent = `Velg ${labelText.toLowerCase()}`;
    select.appendChild(placeholder);

    options.forEach((option) => {
      const value = optionValue(option);
      const label = optionLabel(option);
      const available = option.available !== false;

      const optionElement = document.createElement("option");
      optionElement.value = value;
      optionElement.disabled = !available;
      optionElement.textContent = available
        ? `${label}${suffix}`
        : `${label}${suffix} – ikke tilgjengelig`;
      optionElement.selected =
        available && normalize(value) === normalize(selectedValue);

      select.appendChild(optionElement);
    });

    wrapper.appendChild(select);
    field.appendChild(wrapper);
  }

  function renderField(field, options, selectedValue) {
    if (!Array.isArray(options) || options.length === 0) {
      field.hidden = true;
      field.replaceChildren();
      return;
    }

    const labelText = field.dataset.optionLabel || "";
    const type = field.dataset.optionType || "block";
    const suffix = field.dataset.optionSuffix || "";

    field.replaceChildren();
    field.hidden = false;

    createLabel(field, labelText);

    if (type === "dropdown") {
      createDropdown(field, options, selectedValue, labelText, suffix);
    } else {
      createButtons(field, options, selectedValue, suffix);
    }
  }

  // Regner ut hvilke verdier som er tilgjengelige ut fra valgene over feltet.
  function getOptionStates(data, fieldConfig, currentProduct, activeFields) {
    const configuredOptions = data[fieldConfig.source] || [];

    if (!configuredOptions.length) return [];

    const fieldIndex = activeFields.findIndex((field) => {
      return field.key === fieldConfig.key;
    });

    // Each field is constrained only by the selections above it.
    // This prevents a later choice from silently changing an earlier one.
    const previousFields = activeFields.slice(0, fieldIndex);

    const matchingProducts = data.products.filter((product) => {
      return previousFields.every((previousField) => {
        const selectedValue = normalize(currentProduct?.[previousField.key]);

        if (!selectedValue) return true;

        return normalize(product[previousField.key]) === selectedValue;
      });
    });

    const availableValues = new Set(
      matchingProducts
        .map((product) => normalize(product[fieldConfig.key]))
        .filter(Boolean),
    );

    return configuredOptions.map((option) => {
      return {
        value: optionValue(option),
        label: optionLabel(option),
        available: availableValues.has(normalize(optionValue(option))),
      };
    });
  }

  function renderSelector(selector, data) {
    const currentProduct = getCurrentProduct(data, selector) || {};
    const activeFields = getActiveFields(selector, data);

    activeFields.forEach((fieldConfig) => {
      const field = selector.querySelector("#" + fieldConfig.id);

      if (!field) return;

      const options = getOptionStates(
        data,
        fieldConfig,
        currentProduct,
        activeFields,
      );

      const selectedValue = currentProduct[fieldConfig.key] ?? "";

      renderField(field, options, selectedValue);
    });
  }

  // Gir poeng til produkter som beholder flest mulig av kundens øvrige valg.
  function scoreCandidate(product, state, changedKey, activeFields) {
    let score = 0;

    activeFields.forEach((field) => {
      const key = field.key;
      const selectedValue = normalize(state[key]);

      if (!selectedValue || key === changedKey) return;

      if (normalize(product[key]) === selectedValue) {
        score += 10;
      }
    });

    return score;
  }

  function chooseBestCandidate(candidates, state, changedKey, activeFields) {
    if (!candidates.length) return null;

    return candidates
      .map((product, index) => {
        return {
          product,
          index,
          score: scoreCandidate(product, state, changedKey, activeFields),
        };
      })
      .sort((a, b) => {
        if (b.score !== a.score) {
          return b.score - a.score;
        }

        return a.index - b.index;
      })[0].product;
  }

  // Finner nærmeste gyldige produkt etter at kunden endrer et valg.
  function findBestMatch(data, state, changedKey, activeFields) {
    const changedValue = normalize(state[changedKey]);

    if (!changedValue) return null;

    const changedIndex = activeFields.findIndex((field) => {
      return field.key === changedKey;
    });

    if (changedIndex < 0) return null;

    const requiredFields = activeFields.slice(0, changedIndex + 1);

    // Require the changed value and every selection above it.
    // Values below the changed field may vary, and scoring tries to
    // preserve as many of them as possible.
    const candidates = data.products.filter((product) => {
      return requiredFields.every((field) => {
        const selectedValue = normalize(state[field.key]);

        if (!selectedValue) return true;

        return normalize(product[field.key]) === selectedValue;
      });
    });

    return chooseBestCandidate(candidates, state, changedKey, activeFields);
  }

  function getSectionElement(selector) {
    return selector.closest('[id^="shopify-section-"]');
  }

  // Lager produkt-URL fra `url` eller `handle` i JSON-produktet.
  function buildTargetUrl(match) {
    const rawUrl = String(match.url || "").trim();
    const handle = String(match.handle || "").trim();

    if (rawUrl) {
      const url = new URL(rawUrl, window.location.origin);
      url.searchParams.set("view", "custom_ringer");

      return `${url.pathname}${url.search}`;
    }

    if (handle) {
      return `/products/${encodeURIComponent(handle)}` + "?view=custom_ringer";
    }

    console.error("[CUSTOM RING] Product is missing url/handle:", match);

    return null;
  }

  // Bytter produktseksjonen uten en full sideinnlasting.
  async function silentReload(selector, url) {
    const currentSection = getSectionElement(selector);

    if (!currentSection) {
      window.location.assign(url);
      return;
    }

    const currentSectionId = currentSection.id;

    log("Reloading section:", currentSectionId, "from", url);

    try {
      currentSection.classList.add("is-loading");

      const response = await fetch(url, {
        credentials: "same-origin",
        headers: {
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (!response.ok) {
        throw new Error(`Product fetch failed: ${response.status}`);
      }

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, "text/html");

      const newSection = doc.getElementById(currentSectionId);

      if (!newSection) {
        log(
          "Could not find matching section in fetched HTML, fallback redirect",
        );

        window.location.assign(url);
        return;
      }

      currentSection.replaceWith(newSection);
      window.history.pushState({}, "", url);

      document.dispatchEvent(
        new CustomEvent("shopify:section:load", {
          detail: {
            sectionId: currentSectionId.replace("shopify-section-", ""),
          },
        }),
      );

      initCustomRingerSelector(document);
    } catch (error) {
      console.error("[CUSTOM RING] Silent reload failed:", error);

      window.location.assign(url);
    }
  }

  async function handleChange(selector, changedKey) {
    try {
      const data = selector._customRingerData;
      if (!data) return;

      const state = getState(selector);

      log("Changed field:", changedKey);
      log("Current state:", state);

      const match = findBestMatch(
        data,
        state,
        changedKey,
        getActiveFields(selector, data),
      );

      log("Selected product:", match);

      if (!match) {
        log("No product found for changed option");
        return;
      }

      if (normalize(match.sku) === normalize(selector.dataset.currentSku)) {
        log("Already on selected product");
        return;
      }

      const targetUrl = buildTargetUrl(match);

      if (!targetUrl) {
        log("Missing sku/url in JSON entry");
        return;
      }

      log("Target URL:", targetUrl);

      await silentReload(selector, targetUrl);
    } catch (error) {
      console.error("[CUSTOM RING] handleChange failed:", error);
    }
  }

  // Lytter etter klikk på knapper og endringer i dropdown-felt.
  function bindSelector(selector) {
    if (!selector || selector.dataset.ready === "true") {
      return;
    }

    selector.dataset.ready = "true";

    selector.addEventListener("click", function (event) {
      const button = event.target.closest(".gifteringer-selector__option");

      if (!button || !selector.contains(button) || button.disabled) {
        return;
      }

      const field = button.closest("[data-option-key]");

      const group = button.closest(".gifteringer-selector__options");

      if (!field || !group) return;

      group
        .querySelectorAll(".gifteringer-selector__option")
        .forEach((item) => {
          item.classList.remove("is-active");
        });

      button.classList.add("is-active");

      handleChange(selector, field.dataset.optionKey);
    });

    selector.addEventListener("change", function (event) {
      const select = event.target.closest(".gifteringer-selector__select");

      if (!select || !selector.contains(select)) {
        return;
      }

      const field = select.closest("[data-option-key]");

      if (!field) return;

      handleChange(selector, field.dataset.optionKey);
    });
  }

  // Laster data, tegner velgeren og binder hendelser én gang per seksjon.
  async function prepareSelector(selector) {
    if (!selector || selector.dataset.initialized === "true") {
      return;
    }

    const jsonUrl = selector.dataset.jsonUrl;

    if (!jsonUrl) {
      log("Missing data-json-url");
      return;
    }

    selector.dataset.initialized = "true";

    try {
      const data = await loadJSON(jsonUrl);
      selector._customRingerData = data;

      renderSelector(selector, data);
      bindSelector(selector);
    } catch (error) {
      selector.dataset.initialized = "false";

      console.error("[CUSTOM RING] Initialization failed:", error);
    }
  }

  // Initialiserer alle ringvelgere på siden.
  function initCustomRingerSelector(root) {
    const scope = root || document;

    scope.querySelectorAll(".gifteringer-selector").forEach((selector) => {
      prepareSelector(selector);
    });
  }

  document.addEventListener("DOMContentLoaded", function () {
    initCustomRingerSelector(document);
  });

  document.addEventListener("shopify:section:load", function () {
    initCustomRingerSelector(document);
  });

  window.addEventListener("popstate", function () {
    window.location.reload();
  });
})();